import { isPaxtonBrand } from '@/util/enterprise'
import { kProduction } from './constants-ui'
import { kHasEnabledExternalStorageProviders } from './constants-external-storage'

/**
 * Returns `true` if the app is running in a non-production environment.
 * Used for enabling features meant only for staging or local development.
 */
function kFeatureFlagNonProduction(): boolean {
  return !kProduction
}

/**
 * Feature Flags
 *
 * List of feature flags available in the application.
 */
export enum kFeatureFlags {
  WEBSOCKET_CHAT = 'WEBSOCKET_CHAT',
  /** External document management system connections on Settings. */
  DRIVE_SYNC = 'DRIVE_SYNC',
  /** Manual local testing toggle: show external-storage files and folders in the Matter files drive. */
  DRIVE_SHOW_EXTERNAL_STORAGE_ITEMS = 'DRIVE_SHOW_EXTERNAL_STORAGE_ITEMS',
  /** Whether the new user-files experience is active (what's shown and which pipeline is used). When false, the legacy files experience is used. */
  NEW_FILES_UX = 'NEW_FILES_UX',
  /** Templates tab in file-picker/move/select-template modals (not yet supported). */
  TEMPLATES_TAB = 'TEMPLATES_TAB',
  /** Add Template button in the artifact editor toolbar (upload/select a .docx template). */
  ARTIFACT_TEMPLATES = 'ARTIFACT_TEMPLATES',
  /** Matter sharing and collaboration features. */
  COLLABORATION = 'COLLABORATION',
  /** Lets accepted matter collaborators view the matter's Medical Chronology. */
  COLLABORATOR_MEDCHRON_ACCESS = 'COLLABORATOR_MEDCHRON_ACCESS',
  /** Userpilot checklist tour card for sample matters on the matter overview page. */
  SAMPLE_MATTER_TOUR = 'SAMPLE_MATTER_TOUR',
  /**
   * Writing samples on the new user_files pipeline (upload / select-from-matter as user_files,
   * sent as `writing_sample_files`). Gated separately from NEW_FILES_UX so the rollout can be
   * decided independently of the Template<>Writing-Sample synergy work. When off, the template
   * tab keeps the legacy `template_paths` flow.
   */
  WRITING_SAMPLE_FILES = 'WRITING_SAMPLE_FILES',
}

/*
 * New files experience: on for the Paxton brand in all environments.
 * - Haiku / non-Paxton brands stay on legacy.
 * - To work on the legacy UX, flip this to false locally or in a preview deploy.
 */
const kNewFilesUxEnabled = isPaxtonBrand()

/*
 * Collaboration is a permanent capability of the Matter-centered, new-files experience and is only
 * available for the Paxton brand. This coupling will never change — collaboration does not run on
 * the legacy files experience — so every collaboration sub-flag derives from this single base.
 *
 * The feature-specific collaboration flags below represent collaborator permissions (whether a
 * collaborator may access a capability), not the release status of the underlying features.
 */
const kCollaborationEnabled = kNewFilesUxEnabled && isPaxtonBrand()

/**
 * Feature Flag Statuses
 *
 * Defines the enabled/disabled status of each feature flag.
 */
const kFeatureFlagStatus = {
  [kFeatureFlags.WEBSOCKET_CHAT]: kFeatureFlagNonProduction(),
  [kFeatureFlags.DRIVE_SYNC]: kNewFilesUxEnabled && kHasEnabledExternalStorageProviders,
  [kFeatureFlags.DRIVE_SHOW_EXTERNAL_STORAGE_ITEMS]: false,
  [kFeatureFlags.NEW_FILES_UX]: kNewFilesUxEnabled,
  // kNewFilesUxEnabled couples templates to the new files pipeline they depend on.
  // isPaxtonBrand() pins the Haiku exclusion independently for redundancy.
  [kFeatureFlags.ARTIFACT_TEMPLATES]: kNewFilesUxEnabled && isPaxtonBrand(),
  [kFeatureFlags.TEMPLATES_TAB]: false,
  [kFeatureFlags.COLLABORATION]: kCollaborationEnabled,
  // Medical Chronology is already released; this flag controls whether accepted collaborators may
  // access the matter-level chronology. Non-production only until ready — flip to
  // `kCollaborationEnabled` to release to prod.
  [kFeatureFlags.COLLABORATOR_MEDCHRON_ACCESS]: kCollaborationEnabled && kFeatureFlagNonProduction(),
  [kFeatureFlags.SAMPLE_MATTER_TOUR]: isPaxtonBrand(),
  // Non-production only for now; the writing-sample entry points additionally require the new
  // files UX (they produce user_files), so callers gate on `NEW_FILES_UX && WRITING_SAMPLE_FILES`.
  [kFeatureFlags.WRITING_SAMPLE_FILES]: kFeatureFlagNonProduction(),
}

/**
 * Get Feature Flag Status
 *
 * Returns the current enabled/disabled status of a given feature flag.
 * Use this in application logic to conditionally enable features.
 *
 * @param flag - The feature flag to check
 * @returns `true` if the feature is enabled, `false` otherwise
 */
export function kGetFeatureFlagStatus(flag: kFeatureFlags): boolean {
  return kFeatureFlagStatus[flag]
}
